import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Maximize2 } from "lucide-react";
import Map from "@zeliot/core/base/modules/TrackingControls/Maps/Map";
import { useTranslation } from "src/pages";


const VIGILANCE_LEVELS = [
  {
    key: "green",
    label: "Vert",
    color: "#22c55e",
    description: "Aucun risque particulier, meteo habituelle.",
  },
  {
    key: "yellow",
    label: "Jaune",
    color: "#eab308",
    description: "Phenomenes habituels mais potentiellement dangereux.",
  },
  {
    key: "orange",
    label: "Orange",
    color: "#f97316",
    description: "Phenomenes dangereux a intensite elevee.",
  },
  {
    key: "red",
    label: "Rouge",
    color: "#ef4444",
    description: "Phenomenes dangereux d'intensite exceptionnelle.",
  },
];

const ZONE_COLOR_PALETTE = VIGILANCE_LEVELS.map((level) => level.color);

const MapOverviewTraffic = forwardRef(
  (
    {
      openModal,
      allAreas = [],
      site = null,
      zone = null,
      weekdayFilter = "all",
      onWeekdayFilterChange,
      weekdayOptions = [],
    },
    ref
  ) => {
    const [map, setMap] = useState(null);
    const [displayAoIs, setDisplayAoIs] = useState(allAreas || []);
    const [selectedAoiId, setSelectedAoiId] = useState(null);
    const [selectedVigilance, setSelectedVigilance] = useState("all");
    const [legendPage, setLegendPage] = useState(1);
    const drawDepartmentRef = useRef(null);
    const drawShapeRef = useRef(null);
    const drawAllAoisRef = useRef(null);
    const LEGEND_PAGE_SIZE = 14;

    useEffect(() => {
      const zoneId = zone?.zone?.id ?? null;
      const siteAreaId = site?.area?.id ?? null;
      setSelectedAoiId(null);
      setSelectedVigilance("all");

      if (zoneId) {
        setDisplayAoIs([zone.zone]);
        return;
      }

      if (siteAreaId && site?.area?.name !== "Overview") {
        setDisplayAoIs([site.area]);
        return;
      }

      setDisplayAoIs(allAreas || []);
    }, [allAreas, site, zone]);

    useImperativeHandle(ref, () => ({
      handleSetDrawShape: (...args) => drawShapeRef.current?.(...args),
      handleSetDrawAllAois: (...args) => drawAllAoisRef.current?.(...args),
      getMap: () => map,
    }));

    const hasGoogle = typeof window !== "undefined" && !!window.google;
    const allStyledAoIs = (displayAoIs || []).map((aoi, index) => {
      const vigilance = VIGILANCE_LEVELS[index % VIGILANCE_LEVELS.length];
      const color = vigilance.color;
      return {
        ...aoi,
        vigilanceKey: vigilance.key,
        strokeColor: color,
        fillColor: color,
        fillOpacity: 0.22,
      };
    });
    const vigilanceFilteredAoIs =
      selectedVigilance === "all"
        ? allStyledAoIs
        : allStyledAoIs.filter((aoi) => aoi.vigilanceKey === selectedVigilance);

    const styledAoIs = selectedAoiId
      ? vigilanceFilteredAoIs.filter((aoi) => String(aoi?.id) === String(selectedAoiId))
      : vigilanceFilteredAoIs;

    const legendItems = vigilanceFilteredAoIs.map((aoi, index) => {
      return {
        id: aoi?.id ?? `aoi-${index}`,
        name: aoi?.name || `Zone ${index + 1}`,
        color: aoi.strokeColor || ZONE_COLOR_PALETTE[index % ZONE_COLOR_PALETTE.length],
        value: "0%",
      };
    });
    const legendTotalPages = Math.max(1, Math.ceil(legendItems.length / LEGEND_PAGE_SIZE));
    const legendStart = (legendPage - 1) * LEGEND_PAGE_SIZE;
    const legendPageItems = legendItems.slice(legendStart, legendStart + LEGEND_PAGE_SIZE);

    useEffect(() => {
      setLegendPage(1);
    }, [selectedVigilance, allAreas, site, zone]);

    useEffect(() => {
      if (legendPage > legendTotalPages) {
        setLegendPage(legendTotalPages);
      }
    }, [legendPage, legendTotalPages]);
const {t} = useTranslation ();
    return (
      <div className="rounded-2xl bg-card border border-border shadow-sm p-4">
        <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base md:text-lg font-semibold text-foreground tracking-tight">
  {t("LEWOOMINING.traffic_weather")}
</h3>

          <button className="p-1.5 rounded hover:bg-secondary" onClick={openModal}>
            <Maximize2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        {Array.isArray(weekdayOptions) && weekdayOptions.length > 0 ? (
          <div className="mb-3">
            <select
              value={weekdayFilter}
              onChange={(e) => onWeekdayFilterChange?.(e.target.value)}
              className="h-10 min-w-[180px] rounded-lg border border-border bg-card px-3 pr-8 text-sm leading-5 text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {weekdayOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="rounded-xl border border-border bg-secondary/20 p-3">
          <div className="space-y-4">
            <div className="relative h-[520px] rounded-xl overflow-hidden border border-border bg-gradient-to-br from-blue-900/50 to-blue-800/30">
              <div className="absolute inset-0">
                {hasGoogle ? (
                  <Map
                    google={window.google}
                    setMap={(m) => setMap(m)}
                    registerDrawFunction={(fn) => {
                      drawDepartmentRef.current = fn;
                    }}
                    zoom={6}
                    setDrawShape={(fn) => {
                      drawShapeRef.current = fn;
                    }}
                    setDrawAllAois={(fn) => {
                      drawAllAoisRef.current = fn;
                    }}
                    onAoiClick={(clickedAoi) => {
                      const clickedId = clickedAoi?.id;
                      if (!clickedId) return;
                      setSelectedAoiId((prev) =>
                        String(prev) === String(clickedId) ? null : clickedId
                      );
                    }}
                    area={site?.area}
                    aois={styledAoIs}
                    containerClassName="google-map-list-aoi"
                  />
                ) : (
                  <div className="h-full w-full grid place-items-center text-xs text-muted-foreground bg-card/40">
                    {t("LEWOOMINING.map_loading")}
                  </div>
                )}
              </div>

              <div className="absolute inset-y-0 left-0 z-10 hidden w-[292px] border-r border-border/80 bg-card/95 shadow-2xl backdrop-blur supports-[backdrop-filter]:bg-card/88 lg:flex lg:flex-col">
                <div className="border-b border-border px-4 py-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <p className="text-base font-semibold text-foreground">{t("LEWOOMINING.legend")}</p>
                      <p className="text-xs text-muted-foreground">{legendItems.length} zones</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedAoiId(null);
                        setSelectedVigilance("all");
                      }}
                      className="rounded-md border border-border bg-card px-2 py-1 text-xs text-muted-foreground hover:bg-secondary/40"
                    >
                      Reset
                    </button>
                  </div>
                  <div className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-muted-foreground">
                    {t("LEWOOMINING.all_zones")}
                  </div>
                </div>

                <div className="flex-1 overflow-auto px-3 py-3">
                  <div className="space-y-2">
                    <button
                      type="button"
                      className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors ${
                        selectedAoiId == null
                          ? "border-slate-500 bg-slate-100"
                          : "border-border bg-card hover:bg-secondary/40"
                      }`}
                      onClick={() => setSelectedAoiId(null)}
                    >
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-500 text-[10px] font-bold text-white">
                        All
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                        {t("LEWOOMINING.all_zones")}
                      </span>
                    </button>
                    {legendPageItems.map((zoneItem) => (
                      <button
                        key={`legend-${zoneItem.id}`}
                        type="button"
                        className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors ${
                          String(selectedAoiId) === String(zoneItem.id)
                            ? "border-slate-500 bg-slate-100"
                            : "border-border bg-card hover:bg-secondary/40"
                        }`}
                        onClick={() =>
                          setSelectedAoiId((prev) =>
                            String(prev) === String(zoneItem.id) ? null : zoneItem.id
                          )
                        }
                      >
                        <span
                          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 bg-white text-[9px] font-bold"
                          style={{ borderColor: zoneItem.color, color: zoneItem.color }}
                        >
                          {zoneItem.value}
                        </span>
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: zoneItem.color }} />
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                          {zoneItem.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {legendItems.length > LEGEND_PAGE_SIZE && (
                  <div className="border-t border-border px-3 py-3">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <button
                        type="button"
                        onClick={() => setLegendPage((prev) => Math.max(1, prev - 1))}
                        disabled={legendPage === 1}
                        className="rounded-md border border-border bg-card px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {t("LEWOOMINING.previous")}
                      </button>
                      <span>
                        Page {legendPage}/{legendTotalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setLegendPage((prev) => Math.min(legendTotalPages, prev + 1))}
                        disabled={legendPage === legendTotalPages}
                        className="rounded-md border border-border bg-card px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {t("LEWOOMINING.next")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="text-sm">
                <p className="font-semibold text-foreground mb-2">
  {t("LEWOOMINING.filter_applied")}
</p>
<div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedVigilance("all");
                      setSelectedAoiId(null);
                    }}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-muted-foreground transition-colors ${
                      selectedVigilance === "all"
                        ? "border-slate-500 bg-slate-100"
                        : "border-border bg-card hover:bg-secondary/40"
                    }`}
                    title="Afficher toutes les vigilances"
                  >
                    {t("LEWOOMINING.label.all")}
                  </button>
                  {VIGILANCE_LEVELS.map((level) => (
                    <button
                      key={`vigilance-${level.key}`}
                      type="button"
                      onClick={() => {
                        setSelectedVigilance(level.key);
                        setSelectedAoiId(null);
                      }}
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-muted-foreground transition-colors ${
                        selectedVigilance === level.key
                          ? "border-slate-500 bg-slate-100"
                          : "border-border bg-card hover:bg-secondary/40"
                      }`}
                      title={level.description}
                    >
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: level.color }} />
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>
          </div>
        </div>
      </div>
    );
  }
);

export default MapOverviewTraffic;
